import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_LAYOUT,
  historyOpenedModel,
  isLayoutKey,
  loadLayoutModel,
  saveLayoutModel,
  toggled,
  withDock,
  withHistorySide,
  withHistoryStandalone,
  withVisible,
  type LayoutModel,
  type PanelName,
  type Side,
} from "./layoutModel.ts";

const storage = (() => {
  try {
    return localStorage;
  } catch {
    return undefined;
  }
})();

/** Forgets every stored layout (model + panel sizes of every arrangement, current and previous versions). */
export function resetLayouts(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && isLayoutKey(k)) localStorage.removeItem(k);
    }
  } catch {
    // ignore
  }
}

/**
 * State of the IDE panels, shared by the header controls and the layout. The validated layout model is the single
 * source of truth: which of Files / Code / Tools / Agent are visible, whether History is standalone and on which side,
 * and where Files and Agent are docked. It is persisted per browser (versioned; stale or corrupt values fall back to
 * the canonical default). Hiding a panel never unmounts its content (see PanelHost): open tabs, unsaved edits,
 * terminals, the chat draft and the Explorer tree are kept.
 */
export function useIdeLayout() {
  const [model, setModel] = useState<LayoutModel>(() => loadLayoutModel(storage));
  /** Bumped by "Reset layout" so the panel groups remount and re-read the (now cleared) stored sizes. */
  const [generation, setGeneration] = useState(0);

  useEffect(() => saveLayoutModel(storage, model), [model]);

  const toggle = useCallback((p: PanelName) => setModel((m) => toggled(m, p)), []);
  const show = useCallback((p: PanelName) => setModel((m) => withVisible(m, p, true)), []);
  const hide = useCallback((p: PanelName) => setModel((m) => withVisible(m, p, false)), []);
  const dock = useCallback((p: "files" | "agent", side: Side) => setModel((m) => withDock(m, p, side)), []);
  const setHistoryStandalone = useCallback(
    (standalone: boolean) =>
      setModel((m) => (standalone ? historyOpenedModel(m) : withHistoryStandalone(m, false))),
    [],
  );
  const setHistorySide = useCallback((side: Side) => setModel((m) => withHistorySide(m, side)), []);
  const reset = useCallback(() => {
    resetLayouts();
    setModel(DEFAULT_LAYOUT);
    setGeneration((g) => g + 1);
  }, []);

  return { model, generation, toggle, show, hide, dock, setHistoryStandalone, setHistorySide, reset };
}
export type IdeLayout = ReturnType<typeof useIdeLayout>;
